import {
  isCancelledError,
  type QueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import {
  getSessionState,
  loginBySessionConfig,
  transitionSession,
  requireAuthentication,
} from '@/entities/user/session';
import { LoginStatus } from '@/entities/user/user';
import {
  getErrorStatus,
  type InterceptedError,
} from '@/shared/api/intercepted-error';
import { requestError } from '@/shared/ui/notifications';
import { fetchCachedStorages } from '@/shared/lib/utils/fetch-cached-storages';
import { BACKEND_UNAVAILABLE_RELOAD_GUARD } from '@/shared/config';
import { PREFETCHABLE_QUERIES } from './prefetchable-queries';

// TODO(auth-flow): Revisit redirect presets for initial login preparation and logout
// if a dedicated startup flow is needed. Cover session restoration, next/retry,
// and loader exclusions; keep this operation reusable by modal reauthentication
// without navigation so the current page and unsaved drafts stay mounted.
export async function prepareApplication(
  queryClient: QueryClient,
  queries: UseQueryOptions[] = PREFETCHABLE_QUERIES,
): Promise<boolean> {
  const status = getSessionState(queryClient).loginStatus;
  if (status === LoginStatus.login) return true;
  if (
    ![
      LoginStatus.pending,
      LoginStatus.preparing,
      LoginStatus.preparationError,
    ].includes(status)
  )
    return false;

  try {
    return await queryClient.fetchQuery({
      queryKey: ['application-preparation'],
      cacheTime: Infinity,
      staleTime: Infinity,
      retry: false,
      queryFn: async () => {
        const state = transitionSession(queryClient, LoginStatus.preparing);
        try {
          await fetchCachedStorages(queryClient, [
            loginBySessionConfig,
            ...queries,
          ]);
          if (getSessionState(queryClient) !== state) return false;
          sessionStorage.removeItem(BACKEND_UNAVAILABLE_RELOAD_GUARD);
          transitionSession(queryClient, LoginStatus.login);
          return true;
        } catch (error) {
          if (
            getErrorStatus(error) === 401 &&
            getSessionState(queryClient).loginStatus !== LoginStatus.loggingOut
          )
            requestError(error as InterceptedError);
          if (
            getSessionState(queryClient) === state &&
            !isCancelledError(error)
          ) {
            if (getErrorStatus(error) === 401)
              requireAuthentication(queryClient);
            else transitionSession(queryClient, LoginStatus.preparationError);
          }
          throw error;
        }
      },
    });
  } catch {
    // Preparation failures belong to the recovery screen, never the login mutation.
    return false;
  }
}
